/**
 * Generic repeating-section list helpers (#996 Phase 2.5).
 *
 * Every repeating section (Small UPS, Cooling, and future Liquid Cooling /
 * Facility Power) shares the same status grouping, in-section reorder, session
 * patch-merge, duplicate-naming, validation, draft, and legacy-row-synthesis
 * logic. This factory centralizes that core; each section's `*ListUtils.ts`
 * supplies a small config and re-exports the returned functions under its
 * existing public names (keeping all call sites + tests untouched).
 *
 * Section-specific table-cell formatting / sublines / column key sets stay in
 * the per-section module since they are inherently field-shaped.
 */

/** Client-only id for a unit synthesized from legacy flat request columns. */
export const REPEATABLE_SECTION_SYNTHETIC_ID = -1;

export type RepeatableSectionStatus = 'existing' | 'new';

/** Minimal shape every repeating-section unit satisfies (camelCase). */
export interface RepeatableSectionUnit {
  id?: number;
  requestId: number;
  status: RepeatableSectionStatus;
  name?: string;
  displayOrder: number;
}

export interface GroupedRepeatableUnits<TUnit> {
  existing: TUnit[];
  new: TUnit[];
}

export type RepeatableListSection = 'existing' | 'new';

/** Per-section configuration injected into the generic list-utils factory. */
export interface RepeatableListUtilsConfig<
  TUnit extends RepeatableSectionUnit,
> {
  /** DEFAULT_*_CONFIGURATION used to seed draft units. */
  defaultConfiguration: Partial<TUnit>;
  /** Client serial auto-namer (seed + already-taken names). */
  generateAutoName: (
    seed: number,
    takenNames?: Iterable<string | undefined>,
  ) => string;
  /** Whether a name looks auto-generated (so duplicates re-serialize). */
  isAutoGeneratedName: (name: string | undefined) => boolean;
  /** Prefix for "X N" fallbacks, e.g. 'UPS ' or 'Cooling System '. */
  namePrefix: string;
  /** Editor chrome label when creating new, e.g. 'New small UPS'. */
  newLabel: string;
  /** Noun used in duplicate-name validation message, e.g. 'Small UPS'. */
  nameNoun: string;
  /** Secondary display-name fallback (e.g. manufacturer+model, or description). */
  getDisplayNameFallback: (unit: TUnit) => string | undefined;
  /** Legacy flat request keys that, if present, synthesize a single row. */
  legacySourceKeys: readonly string[];
  /** Map flat request source → unit fields (sans id/status/displayOrder). */
  mapLegacyFields: (source: Record<string, unknown>) => Partial<TUnit>;
}

/** Parse numeric form input — returns undefined for empty or non-finite values. */
export function parseOptionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function normalizeNameKey(name: string | undefined): string | null {
  const trimmed = name?.trim();
  return trimmed ? trimmed.toLowerCase() : null;
}

/**
 * Build the generic list-utils bundle for one repeating section. The returned
 * function names are intentionally generic; section modules alias them to their
 * public names (e.g. `groupByStatus` → `groupSmallUpsByStatus`).
 */
export function createRepeatableSectionListUtils<
  TUnit extends RepeatableSectionUnit,
  TUpdate extends { name?: string; displayOrder?: number } = Partial<TUnit>,
>(config: RepeatableListUtilsConfig<TUnit>) {
  const syntheticId = REPEATABLE_SECTION_SYNTHETIC_ID;

  function normalizeStatus(
    status: RepeatableSectionStatus | string | undefined,
  ): RepeatableSectionStatus {
    return status === 'existing' ? 'existing' : 'new';
  }

  function groupByStatus(units: TUnit[]): GroupedRepeatableUnits<TUnit> {
    return {
      existing: units.filter(
        unit => normalizeStatus(unit.status) === 'existing',
      ),
      new: units.filter(unit => normalizeStatus(unit.status) === 'new'),
    };
  }

  function sortByDisplayOrder(units: TUnit[]): TUnit[] {
    return [...units].sort(
      (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0),
    );
  }

  /** Reorder units within EXISTING or NEW section; reassigns global displayOrder. */
  function applySectionReorder(
    sortedUnits: TUnit[],
    section: RepeatableListSection,
    fromIndex: number,
    toIndex: number,
  ): TUnit[] {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) {
      return sortedUnits;
    }

    const grouped = groupByStatus(sortedUnits);
    const existing = sortByDisplayOrder(grouped.existing);
    const newUnits = sortByDisplayOrder(grouped.new);
    const sectionList = section === 'existing' ? existing : newUnits;

    if (
      fromIndex >= sectionList.length ||
      toIndex >= sectionList.length ||
      sectionList.length < 2
    ) {
      return sortedUnits;
    }

    const reorderedSection = [...sectionList];
    const [moved] = reorderedSection.splice(fromIndex, 1);
    reorderedSection.splice(toIndex, 0, moved);

    const merged =
      section === 'existing'
        ? [...reorderedSection, ...newUnits]
        : [...existing, ...reorderedSection];

    return merged.map((unit, index) => ({
      ...unit,
      displayOrder: index + 1,
    }));
  }

  /** Merge editor save payload onto a session unit without wiping unset fields. */
  function applySessionPatch(unit: TUnit, patch: TUpdate): TUnit {
    const merged: TUnit = { ...unit };
    (Object.keys(patch) as Array<keyof TUpdate>).forEach(key => {
      const value = patch[key];
      if (value !== undefined) {
        (merged as unknown as Record<string, unknown>)[key as string] = value;
      }
    });
    merged.status = normalizeStatus(merged.status);
    return merged;
  }

  function idsEqual(
    left: number | undefined,
    right: number | undefined,
  ): boolean {
    if (left == null || right == null) {
      return false;
    }
    return Number(left) === Number(right);
  }

  /** NAME column primary — user-defined name, else "<prefix>N" by display order. */
  function getNamePrimaryDisplay(unit: TUnit): string {
    const name = unit.name?.trim();
    if (name) {
      return name;
    }
    if (unit.displayOrder != null && unit.displayOrder > 0) {
      return `${config.namePrefix}${unit.displayOrder}`;
    }
    return '—';
  }

  /** Editor chrome title — user name, else "<prefix>N", else the new-label. */
  function getEditChromeTitle(
    name: string | undefined,
    options: {
      isNew?: boolean;
      displayOrder?: number;
      positionIndex?: number;
    } = {},
  ): string {
    const trimmed = name?.trim();
    if (trimmed) {
      return trimmed;
    }
    if (options.isNew) {
      return config.newLabel;
    }
    const position =
      options.positionIndex != null
        ? options.positionIndex + 1
        : options.displayOrder;
    if (position != null && position > 0) {
      return `${config.namePrefix}${position}`;
    }
    return config.newLabel;
  }

  /**
   * Primary list title — user-defined name, then section fallback, then
   * "<prefix>N" when displayOrder is available. Returns null when none apply.
   */
  function getDisplayName(unit: TUnit): string | null {
    const name = unit.name?.trim();
    if (name) {
      return name;
    }

    const fallback = config.getDisplayNameFallback(unit);
    if (fallback) {
      return fallback;
    }

    if (unit.displayOrder != null && unit.displayOrder > 0) {
      return `${config.namePrefix}${unit.displayOrder}`;
    }

    return null;
  }

  /** Unique duplicate name — user names get `-copy`; auto names get a fresh serial. */
  function buildDuplicateName(
    sourceName: string | undefined,
    takenNames: Iterable<string | undefined> = [],
    tempId: number,
  ): string {
    const trimmed = sourceName?.trim();

    if (trimmed && !config.isAutoGeneratedName(trimmed)) {
      const taken = new Set(
        [...takenNames]
          .map(normalizeNameKey)
          .filter((name): name is string => name != null),
      );

      const candidate = `${trimmed}-copy`;
      if (!taken.has(candidate.toLowerCase())) {
        return candidate;
      }

      let suffix = 2;
      while (taken.has(`${trimmed}-copy-${suffix}`.toLowerCase())) {
        suffix += 1;
      }
      return `${trimmed}-copy-${suffix}`;
    }

    return config.generateAutoName(tempId, takenNames);
  }

  /** Assign a serial name when the user did not provide one. */
  function ensureNameForCreate(
    data: TUpdate,
    tempId: number,
    takenNames: Iterable<string | undefined> = [],
  ): TUpdate {
    if (data.name?.trim()) {
      return data;
    }
    return {
      ...data,
      name: config.generateAutoName(tempId, takenNames),
    };
  }

  /** Client-side uniqueness check before persisting session changes. */
  function validateSessionNames(session: TUnit[]): string | null {
    const seen = new Map<string, string>();

    for (const unit of session) {
      const trimmed = unit.name?.trim();
      if (!trimmed) {
        continue;
      }
      const key = trimmed.toLowerCase();
      if (seen.has(key)) {
        return `${config.nameNoun} name "${trimmed}" is already used by another unit in this request. Names must be unique.`;
      }
      seen.set(key, trimmed);
    }

    return null;
  }

  /** Clone fields for duplicate flow (no id/metadata; keeps source status). */
  function cloneForCreate(
    source: TUnit,
    takenNames: Iterable<string | undefined> = [],
    tempId: number,
  ): Partial<TUnit> {
    const {
      id: _id,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      requestId,
      status,
      name: sourceName,
      ...fields
    } = source as TUnit & { createdAt?: unknown; updatedAt?: unknown };
    const duplicateName = buildDuplicateName(sourceName, takenNames, tempId);
    return {
      ...(fields as Partial<TUnit>),
      requestId,
      status: status === 'existing' || status === 'new' ? status : 'new',
      name: duplicateName,
    } as Partial<TUnit>;
  }

  function createDraftConfiguration(
    data: TUpdate,
    displayOrder: number,
    tempId: number,
  ): TUnit {
    return {
      ...config.defaultConfiguration,
      ...data,
      id: tempId,
      displayOrder: data.displayOrder ?? displayOrder,
      status: normalizeStatus((data as { status?: string }).status),
    } as unknown as TUnit;
  }

  /** Whether a list row may offer delete (draft rows or persisted id > 0). */
  function canDeleteFromList(unit: TUnit, isDraftMode: boolean): boolean {
    if (unit.id == null) {
      return false;
    }
    if (isDraftMode) {
      return true;
    }
    return unit.id > 0 && unit.id !== syntheticId;
  }

  function hasLegacyRequestFields(source: Record<string, unknown>): boolean {
    return config.legacySourceKeys.some(key => {
      const value = source[key];
      if (value == null) {
        return false;
      }
      if (typeof value === 'string') {
        return value.trim() !== '';
      }
      if (typeof value === 'number') {
        return value !== 0;
      }
      return true;
    });
  }

  /** Build one display row from pre-multi-instance flat request fields. */
  function buildLegacyFromRequestFields(
    source: Record<string, unknown>,
    requestId: string | number,
  ): TUnit | null {
    if (!hasLegacyRequestFields(source)) {
      return null;
    }

    const numericId =
      typeof requestId === 'number'
        ? requestId
        : parseInt(String(requestId), 10);

    const mapped = config.mapLegacyFields(source) as Record<string, unknown>;
    const displayOrder = 1;

    return {
      ...mapped,
      id: syntheticId,
      requestId: Number.isFinite(numericId) ? numericId : 0,
      status: 'existing',
      displayOrder,
      // #1149 Issue 4 / remaining #2: give the synthesized fallback unit its
      // ordinal label as a real `name` (the same value getDisplayName would
      // fall back to) so the editor's Name field is populated instead of blank.
      name:
        typeof mapped.name === 'string' && mapped.name.trim() !== ''
          ? mapped.name
          : `${config.namePrefix}${displayOrder}`,
    } as unknown as TUnit;
  }

  return {
    SYNTHETIC_ID: syntheticId,
    normalizeStatus,
    groupByStatus,
    sortByDisplayOrder,
    applySectionReorder,
    applySessionPatch,
    idsEqual,
    getNamePrimaryDisplay,
    getEditChromeTitle,
    getDisplayName,
    buildDuplicateName,
    ensureNameForCreate,
    validateSessionNames,
    cloneForCreate,
    parseOptionalNumber,
    createDraftConfiguration,
    canDeleteFromList,
    hasLegacyRequestFields,
    buildLegacyFromRequestFields,
  };
}
